import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Layout, Header } from 'app/theme/layout'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import CateringOccasionsSlider from 'app/theme/sections/CateringOccasionsSlider'
import Calculator from 'app/theme/calculator'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from './shared/OccasionPageGrid'
import { useInView } from 'react-intersection-observer'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import hero from 'assets/img/food-truck-corporate-background.jpg'
import getGoogleMetaTags from './occasionSEOData'
import corporateCatering1 from 'assets/img/corporate-catering-1.jpg'
import corporateCatering2 from 'assets/img/corporate-catering-2.jpg'
import corporateCatering3 from 'assets/img/corporate-catering-3.jpg'
import corporateCatering4 from 'assets/img/corporate-catering-4.jpg'

export default function CorporateCateringPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  const googleMetaTags = getGoogleMetaTags('corporate')

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Corporate Catering with Delicious Food Trucks - Roaming Hunger</title>
        <meta name="description" content="Book food trucks for your next office lunch catering, employee appreciation day, corporate event, or business meeting."></meta>
        <meta name="og:title" content="Corporate Catering with Delicious Food Trucks"></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={hero}
          onButtonClick={openModal}
          title="Corporate Catering"
          description={(
            <span>
              Food trucks are an easy and inexpensive way to celebrate a company
              milestone or retain talent through an employee perk program.
              Engage employees, increase productivity and show everyone on the
              team how valued they are.
            </span>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}
      <Intro />
      <IntroGrid />
      <WhyWorkWithUs padding="pt-10 pb-16" onButtonClick={openModal} />
      <WhyWorkWithUsGrid />
      <CuisineTypes baseUrl='/food-truck-catering' />
      <HowItWorks heading="How Corporate Catering Works" onButtonClick={openModal} />
      <HowItWorksLarge heading="How Corporate Catering Works" onButtonClick={openModal} />
      <FeaturedClients onButtonClick={openModal} />
      <Calculator id="calculator" padding="py-10" onButtonClick={openModal} />
      <FAQ/>
      <CateringOccasionsSlider
        heading="Other catering occasions"
        className="bg-white py-10"
      />
    </Layout>
  )
}

function Intro() {
  return (
    <div className="container text-center pt-12">
      <h2 className="text-navy text-4xl leading-tight font-platform">
        Corporate Catering with Delicious Food Trucks
      </h2>
      <p className="font-light text-gray-900 mt-6">
        Bring food trucks to your next employee or client appreciation day, team
        building exercise or simply to reward your staff with gourmet food.
        Including a catering option in your employee incentive program is a
        great way to motivate your staff.
      </p>
    </div>
  )
}


function IntroGrid() {
  return (
    <OccasionPageGrid className="py-10">
      <OccasionPageGridRow
        heading="Your Company Is Covered"
        content="Get back to doing more important things. Experience our seamless booking with a single point of contact and your corporate catering preferences on file. We handle all the logistics, whether you plan to book food trucks once or over and over again."
        image={corporateCatering1}
        reverse
      />
      <OccasionPageGridRow
        heading="Something for Everyone"
        content="Keep your entire office well fed. With a network of 23,000 food trucks, we secure a variety of food options no matter where you work. From lobster rolls, to tacos or gourmet salads, we ensure a delicious mix designed to make everyone happy."
        image={corporateCatering2}
      />
    </OccasionPageGrid>
  )
}

function WhyWorkWithUsGrid() {
  return (
    <OccasionPageGrid className="py-10">
      <OccasionPageGridRow
        heading="Celebrate Your Hardworking Staff"
        content="Give the whole team a reason to bond. We bring food trucks to your next employee appreciation day and team building exercise, or schedule regular lunch services to reward everyone in the company. You'll be a food truck hero!"
        image={corporateCatering3}
        reverse
      />
      <OccasionPageGridRow
        heading="Book With Confidence"
        content="Book your corporate catering event with confidence knowing we have established relationships with thousands of food trucks. We hold all funds until after your event, so we can guarantee you'll get fantastic customer service every time."
        image={corporateCatering4}
      />
    </OccasionPageGrid>
  )
}

function FAQ() {
  return (
    <div className="bg-navy">
      <div className="container p-6">
        <div className="flex w-full">
          <div className="w-full py-16">
            <h2 className="text-white text-4xl leading-none font-platform text-center">
              Frequently Asked Questions
            </h2>
            <div className="w-full md:w-4/5 mb-2 mt-6 m-auto">
              <h3 className="text-white text-3xl">How much does food truck catering for corporate events cost?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  Food truck catering packages typically range from $1,000-$3,000, depending on guest count, cuisine, and
                  length of service.
                </p>
                <p className="mt-1 font-light">
                  Cuisine affects the costs of your food truck catering more than any other factor. Here
                  are typical per-person prices along with average minimums.
                </p>
                <table className="w-full table-auto border-spacing-px border-collapse border border-navy-300 my-4">
                  <thead>
                    <tr>
                      <th className="text-left p-2 border border-navy-300">Cuisine</th>
                      <th className="text-left p-2 border border-navy-300">Average Price per Person</th>
                      <th className="text-left p-2 border border-navy-300">1-100 Eaters (Minimum)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/taco-truck-catering/" target="blank" className="text-blue-400">Taco Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$15-25</td>
                      <td className="p-2 border border-navy-300 font-light">$1,200</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/bbq-food-truck-catering/" target="blank" className="text-blue-400">BBQ Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$21-34</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/burger-catering-truck/" target="blank" className="text-blue-400">Burger Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$16-27</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/pizza-truck-catering/" target="blank" className="text-blue-400">Pizza Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$20-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Mediterranean Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$18-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Asian Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$18-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Indian Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$18-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Dessert Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$10-20</td>
                      <td className="p-2 border border-navy-300 font-light">$800</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Beverage Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$8-18</td>
                      <td className="p-2 border border-navy-300 font-light">$800</td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-1 font-light">
                  Use our <a href="#calculator" className="text-blue-400 underline">Food Truck Catering Cost Calculator</a>
                </p>
                <p className="mt-1 font-light">
                  Adding drinks or dessert options (with a savory food truck) will increase the price per
                  person by $4-$5.
                </p>
                <p className="mt-1 font-light">
                  If you're having a smaller corporate event of 50 guests or less, you'll probably be affected by the
                  food truck catering minimums, and the price per person will increase.
                </p>
                <p className="mt-1 font-light">
                  Typical service times are 2-3 hours. Additional hours will increase total costs. Travel fees can
                  also affect pricing. Food trucks may charge a per-mile fee if your event is outside
                  their normal travel radius (20-30 miles).
                </p>
                <p className="mt-1 font-light">
                  This data is based on historical Roaming Hunger catering bookings and does not include
                  any taxes or fees.
                </p>
              </div>

              <h3 className="text-white text-3xl mt-6">What are some popular cuisines for corporate food truck catering?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  Here are our top 10 most requested food truck cuisines:
                </p>
                <ul className="pl-2 list-disc list-inside">
                  <li className="font-light">
                    <a href="/food-truck-catering/taco-truck-catering/" target="blank" className="text-blue-400">Tacos</a> (Mexican and Fusion)
                  </li>
                  <li className="font-light">
                    <a href="/food-truck-catering/burger-catering-truck/" target="blank" className="text-blue-400">Burgers</a>
                  </li>
                  <li className="font-light">
                    <a href="/food-truck-catering/bbq-food-truck-catering/" target="blank" className="text-blue-400">BBQ</a>
                  </li>
                  <li className="font-light">
                    <a href="/food-truck-catering/pizza-truck-catering/" target="blank" className="text-blue-400">Pizza</a>
                  </li>
                  <li className="font-light">Sandwiches</li>
                  <li className="font-light">Asian</li>
                  <li className="font-light">Dessert&nbsp;</li>
                  <li className="font-light">Healthy Food</li>
                  <li className="font-light">Mediterranean</li>
                  <li className="font-light">Breakfast</li>
                </ul>
                <p className="mt-1 font-light">
                  We also get many requests from companies looking for Vegetarian, Vegan, and Gluten-Free options, which many food trucks
                  can accommodate.
                </p>
              </div>
              <h3 className="text-white text-3xl mt-6">How do I find available food trucks to hire for my company event?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  If you know the number of employees or guests, the location, and your budget meets the minimum (see above), you can
                  get catering packages and quotes by submitting a catering request.
                </p>
                <p className="mt-1 font-light">
                  Roaming Hunger will send your request to food trucks in your area, and available trucks will submit a catering
                  package with pricing and menu. From there you can choose the best catering package for you.
                </p>
              </div>
              <h3 className="text-white text-3xl mt-6">Why should I use Roaming Hunger to get a food truck for my corporate event?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  Roaming Hunger is the largest website for food trucks and mobile food businesses. Since 2009,
                  we've helped thousands of companies and individuals book food trucks for catering and events.
                  Today, our site has over 23,000 independent food trucks, trailers, carts, and pop-ups.
                </p>
                <ol className="pl-6 list-disc list-outside">
                  <li className="font-light"><strong className="font-black">Variety</strong> - Our network consists of hundreds of local vendors in every major city, totaling 23,000+ nationally.</li>
                  <li className="font-light"><strong className="font-black">Save Time</strong> - Why have 100 conversations to find one food truck when you can have one conversation to find 100 food trucks?!</li>
                  <li className="font-light"><strong className="font-black">Speed</strong> - We will provide every available food truck option if you are ready to book one day or one month out from your event.</li>
                  <li className="font-light"><strong className="font-black">Quality Options</strong> - All the food trucks we work with are gourmet, vetted, licensed, proven, and reliable.</li>
                  <li className="font-light"><strong className="font-black">Service</strong> - Our 100% Roaming Hunger Service Guarantee protects every catering. If the caterer does not show up, we'll get you a replacement caterer or refund 100% of your money.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
